import React, { useState, useEffect } from 'react';
import { Zap, Send, Download, Trash2, Pencil, Calendar, AlertTriangle } from 'lucide-react';
import { useLocation } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import api from '@/utils/api';

const fmt = (val) => Math.round(parseFloat(val || 0)).toLocaleString('en-IN');
const toTitleCase = (str) => (str || '').replace(/\b\w/g, c => c.toUpperCase());

const SalaryProcess1 = ({ user }) => {
  const [salaries, setSalaries] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [costCentres, setCostCentres] = useState({});
  const [empTypes, setEmpTypes] = useState({});
  const [loading, setLoading] = useState(true);
  const [selectedIds, setSelectedIds] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [filterMonth, setFilterMonth] = useState(() => {
    return localStorage.getItem('v2_salary_month') || new Date().toISOString().slice(0, 7);
  });
  const [filterCostCentre, setFilterCostCentre] = useState('');
  const [filterEmpType, setFilterEmpType] = useState('');
  const [empTypeOptions, setEmpTypeOptions] = useState(['Payroll', 'Commission']);
  const location = useLocation();

  useEffect(() => {
    localStorage.setItem('v2_salary_month', filterMonth);
    fetchData();
  }, [filterMonth, location.key]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [empRes, ccRes, typesRes, empTypeOptsRes] = await Promise.all([
        api.get('/employees?status=Active'),
        api.get('/cost-centres'),
        api.get('/employee-types'),
        api.get('/employment-type-options').catch(() => ({ data: [] })),
      ]);
      setEmployees(empRes.data);
      const ccMap = {};
      ccRes.data.forEach(c => { ccMap[c.id] = c.name; });
      setCostCentres(ccMap);
      const typeMap = {};
      typesRes.data.forEach(t => { typeMap[t.id] = t.name; });
      setEmpTypes(typeMap);
      // Build dynamic employment type options
      const defaults = ['Payroll', 'Commission'];
      const custom = (empTypeOptsRes.data || []).map(t => t.name).filter(n => !defaults.includes(n));
      const usedTypes = [...new Set((empRes.data || []).map(e => e.employment_type).filter(Boolean))];
      setEmpTypeOptions([...new Set([...defaults, ...custom, ...usedTypes])]);
      try { await api.post(`/salary-process-v2/generate?month=${filterMonth}&source=grid2`); } catch (_) {}
      const salRes = await api.get(`/salary-process-v2?month=${filterMonth}&source=grid2`);
      setSalaries(salRes.data);
      setSelectedIds([]);
    } catch (error) {
      toast.error('Failed to load data');
    } finally { setLoading(false); }
  };

  const handleRefreshFromAttendance = async (force = false) => {
    setLoading(true);
    try {
      const forceParam = force ? '&force=true' : '';
      const genRes = await api.post(`/salary-process-v2/generate?month=${filterMonth}${forceParam}&source=grid2`);
      if (genRes.data?.records?.length > 0) {
        toast.success(`Generated V2 salary for ${genRes.data.records.length} employees`);
      } else {
        toast.info('No new salary records to generate');
      }
      const salRes = await api.get(`/salary-process-v2?month=${filterMonth}&source=grid2`);
      setSalaries(salRes.data);
      setSelectedIds([]);
    } catch (error) {
      toast.error('Failed to generate salary');
    } finally { setLoading(false); }
  };

  const filteredSalaries = salaries.filter(s => {
    const emp = employees.find(e => e.id === s.employee_id);
    if (filterCostCentre && (!emp || emp.cost_centre_id !== filterCostCentre)) return false;
    if (filterEmpType && (!emp || (emp.employment_type || 'Payroll') !== filterEmpType)) return false;
    return true;
  });

  // Find active employees missing from salary list (no attendance filled)
  const salaryEmpIds = new Set(salaries.map(s => s.employee_id));
  const missingEmployees = employees.filter(emp => {
    if (salaryEmpIds.has(emp.id)) return false;
    if (filterCostCentre && emp.cost_centre_id !== filterCostCentre) return false;
    if (filterEmpType && (emp.employment_type || 'Payroll') !== filterEmpType) return false;
    return true;
  });

  const selectableRows = filteredSalaries;
  const allSelected = selectableRows.length > 0 && selectableRows.every(s => selectedIds.includes(s.id));
  const toggleSelectAll = () => setSelectedIds(allSelected ? [] : selectableRows.map(s => s.id));
  const toggleSelect = (id) => setSelectedIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);

  const [bulkEditOpen, setBulkEditOpen] = useState(false);
  const [bulkEditFields, setBulkEditFields] = useState({});
  const [bulkSaving, setBulkSaving] = useState(false);

  const openBulkEdit = () => {
    if (selectedIds.length === 0) { toast.error('Select at least one employee'); return; }
    setBulkEditFields({});
    setBulkEditOpen(true);
  };

  const handleBulkEditSave = async () => {
    const fields = {};
    Object.entries(bulkEditFields).forEach(([k, v]) => {
      if (v !== '' && v !== undefined && v !== null) fields[k] = parseFloat(v) || 0;
    });
    if (Object.keys(fields).length === 0) { toast.error('No changes to apply'); return; }
    setBulkSaving(true);
    let success = 0;
    for (const id of selectedIds) {
      try { await api.patch(`/salary-process-v2/${id}`, fields); success++; } catch (_) {}
    }
    toast.success(`Updated ${success} record(s)`);
    setBulkSaving(false);
    setBulkEditOpen(false);
    setSelectedIds([]);
    fetchData();
  };

  const handleBulkSubmit = async () => {
    if (selectedIds.length === 0) { toast.error('Select at least one employee'); return; }
    setSubmitting(true);
    let success = 0;
    for (const id of selectedIds) {
      try { await api.post(`/salary-process-v2/${id}/submit`); success++; } catch (_) {}
    }
    toast.success(`${success} record(s) submitted for approval`);
    if (success > 0 && user.role === 'HR') {
      try { await api.post('/notify/hr-submitted', { month: filterMonth, count: success }); } catch (_) {}
    }
    setSubmitting(false);
    setSelectedIds([]);
    fetchData();
  };

  const handleDelete = async (id, name) => {
    if (!window.confirm(`Delete salary record for ${name}?`)) return;
    try { await api.delete(`/salary-process-v2/${id}`); toast.success('Deleted'); fetchData(); }
    catch { toast.error('Failed to delete'); }
  };

  const handleRevert = async (id) => {
    try { await api.post(`/salary-process-v2/${id}/revert-to-draft`); toast.success('Reverted to draft'); fetchData(); }
    catch { toast.error('Failed to revert'); }
  };

  const handleExportExcel = async () => {
    try {
      const params = new URLSearchParams({ month: filterMonth });
      if (filterCostCentre) params.append('cost_centre_id', filterCostCentre);
      if (filterEmpType) params.append('employment_type', filterEmpType);
      const response = await api.get(`/salary-process/export-excel?${params}&source=grid2`, { responseType: 'blob' });
      const blob = new Blob([response.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Salary_V2_${filterMonth}.xlsx`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(url), 5000);
      toast.success('Excel exported');
    } catch (error) {
      toast.error('Failed to export');
    }
  };

  const StatusBadge = ({ status }) => {
    const colors = status === 'Approved' || status === 'Paid'
      ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
      : status === 'Rejected' || status === 'Failed'
      ? 'bg-rose-50 text-rose-700 border-rose-200'
      : status === 'Submitted'
      ? 'bg-blue-50 text-blue-700 border-blue-200'
      : 'bg-amber-50 text-amber-700 border-amber-200';
    return <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold border ${colors}`}>{status}</span>;
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-slate-900"></div></div>;
  }

  const calendarDays = (() => { const [y, m] = filterMonth.split('-').map(Number); return new Date(y, m, 0).getDate(); })();
  const sundaysInMonth = (() => {
    const [y, m] = filterMonth.split('-').map(Number);
    const days = new Date(y, m, 0).getDate();
    let s = 0;
    for (let d = 1; d <= days; d++) { if (new Date(y, m - 1, d).getDay() === 0) s++; }
    return s;
  })();

  const totals = filteredSalaries.reduce((acc, s) => {
    const displayPaidDays = s.dop_days || ((s.days_worked || 0) + (s.weekly_offs || 0) + (s.paid_leaves || 0) - (s.lwp || 0) + (s.odw_days || 0));
    const earnedDays = s.earned_days || ((s.days_worked || 0) + (s.weekly_offs || 0) + (s.paid_leaves || 0) - (s.lwp || 0));
    return {
    basic: acc.basic + (s.basic_salary || 0),
    earned: acc.earned + (s.earned_basic || Math.round((s.basic_salary / (s.total_days || 1)) * earnedDays)),
    ot_amount: acc.ot_amount + (s.ot_amount || 0),
    odw_amount: acc.odw_amount + (s.off_day_amount || 0),
    incentive: acc.incentive + (s.allowances?.Incentive || 0),
    ta: acc.ta + (s.allowances?.['Travel Allowance'] || 0),
    hra: acc.hra + (s.allowances?.HRA || 0),
    da: acc.da + (s.allowances?.DA || s.earned_da || 0),
    conv: acc.conv + (s.allowances?.Conveyance || s.earned_conv || 0),
    gross: acc.gross + (s.gross_salary || 0),
    pf: acc.pf + (s.deductions?.PF || 0),
    esi: acc.esi + (s.deductions?.ESI || 0),
    ptax: acc.ptax + (s.deductions?.PTAX || 0),
    mlwf: acc.mlwf + (s.deductions?.MLWF || 0),
    tds: acc.tds + (s.deductions?.TDS || 0),
    advance: acc.advance + (s.deductions?.Advance || 0),
    other: acc.other + (s.deductions?.Other || 0),
    totalDed: acc.totalDed + (s.total_deductions || 0),
    net: acc.net + (s.net_salary || 0),
  }}, { basic: 0, earned: 0, ot_amount: 0, odw_amount: 0, incentive: 0, ta: 0, hra: 0, da: 0, conv: 0, gross: 0, pf: 0, esi: 0, ptax: 0, mlwf: 0, tds: 0, advance: 0, other: 0, totalDed: 0, net: 0 });

  return (
    <div className="space-y-5" data-testid="salary-process-v2-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-slate-900">Salary Process 1</h1>
          <p className="mt-1 text-base text-slate-600">Earned = Work + W.Off + PL - LWP | Gross = Earned + OT + ODW + Incentive + Advance + HRA + TA + DA + Conv</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          {user.role === 'HR' && (
            <Button onClick={() => handleRefreshFromAttendance(true)} variant="outline" size="sm" data-testid="v2-generate-btn">
              <Zap className="h-4 w-4 mr-1.5" /> Refresh from Attendance
            </Button>
          )}
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm flex items-end justify-between flex-wrap gap-4">
        <div className="flex items-end gap-4 flex-wrap">
          <div>
            <Label>Month</Label>
            <div className="flex items-center gap-2 mt-1">
              <Calendar className="h-4 w-4 text-slate-500" />
              <Input type="month" value={filterMonth} onChange={e => setFilterMonth(e.target.value)} className="w-44" data-testid="v2-month-select" />
              <span className="text-xs text-slate-500">{calendarDays} days</span>
            </div>
          </div>
          <div className="min-w-[180px]">
            <Label>Cost Centre</Label>
            <select value={filterCostCentre} onChange={e => setFilterCostCentre(e.target.value)}
              className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm mt-1" data-testid="v2-cc-filter">
              <option value="">All</option>
              {Object.entries(costCentres).map(([id, name]) => <option key={id} value={id}>{name}</option>)}
            </select>
          </div>
          <div className="min-w-[150px]">
            <Label>Type Of Employee</Label>
            <select value={filterEmpType} onChange={e => setFilterEmpType(e.target.value)}
              className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm mt-1" data-testid="v2-emp-type-filter">
              <option value="">All</option>
              {empTypeOptions.map(t => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button onClick={handleExportExcel} variant="outline" className="border-emerald-300 text-emerald-700 hover:bg-emerald-50" data-testid="v2-export-btn">
            <Download className="h-4 w-4 mr-1.5" /> Export Excel
          </Button>
          {user.role === 'HR' && selectedIds.length > 0 && (
            <div className="flex items-center gap-2">
              <Button onClick={openBulkEdit} variant="outline" className="border-indigo-300 text-indigo-700" data-testid="bulk-edit-btn">
                <Pencil className="h-4 w-4 mr-1.5" /> Edit {selectedIds.length} Selected
              </Button>
              <Button onClick={handleBulkSubmit} disabled={submitting} className="bg-indigo-600 hover:bg-indigo-700" data-testid="v2-submit-btn">
                <Send className="h-4 w-4 mr-1.5" /> {submitting ? 'Sending...' : `Send ${selectedIds.length} for Manager Approval`}
              </Button>
            </div>
          )}
        </div>
      </div>

      {filteredSalaries.length === 0 && missingEmployees.length === 0 ? (
        <div className="bg-white border border-slate-200 rounded-xl p-12 text-center shadow-sm">
          <p className="text-slate-500 mb-2">No V2 salary records for this month.</p>
          <p className="text-xs text-slate-400">Enter attendance in <strong>Monthly Grid 2</strong>, then navigate here.</p>
        </div>
      ) : (
        <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden" data-testid="v2-salary-table">
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="bg-slate-800 text-white">
                  <th className="px-2 py-2 text-center sticky left-0 bg-slate-800 z-10" rowSpan={2}>
                    {user.role === 'HR' && selectableRows.length > 0 && (
                      <Checkbox checked={allSelected} onCheckedChange={toggleSelectAll} className="border-white data-[state=checked]:bg-indigo-500" />
                    )}
                  </th>
                  <th className="px-2 py-2 text-center font-semibold" rowSpan={2}>Sr</th>
                  <th className="px-3 py-2 text-left font-semibold sticky left-8 bg-slate-800 z-10" rowSpan={2}>Employee</th>
                  <th className="px-2 py-2 text-center font-semibold border-l border-slate-600" colSpan={7}>Attendance</th>
                  <th className="px-2 py-2 text-center font-semibold border-l border-slate-600 text-emerald-300" colSpan={10}>Earnings</th>
                  <th className="px-2 py-2 text-center font-semibold border-l border-slate-600 text-rose-300" colSpan={8}>Deductions</th>
                  <th className="px-2 py-2 text-center font-semibold border-l border-slate-600 text-amber-300" rowSpan={2}>Net Pay</th>
                  <th className="px-2 py-2 text-center font-semibold border-l border-slate-600" rowSpan={2}>Status</th>
                </tr>
                <tr className="bg-slate-700 text-slate-200">
                  {/* Attendance */}
                  <th className="px-2 py-1.5 text-right font-medium border-l border-slate-600">Work</th>
                  <th className="px-2 py-1.5 text-right font-medium text-blue-300">W.Off</th>
                  <th className="px-2 py-1.5 text-right font-medium text-emerald-300">PL</th>
                  <th className="px-2 py-1.5 text-right font-medium text-red-300">LWP</th>
                  <th className="px-2 py-1.5 text-right font-medium text-purple-300">ODW</th>
                  <th className="px-2 py-1.5 text-right font-medium font-bold text-cyan-300">D.O.P.</th>
                  <th className="px-2 py-1.5 text-right font-medium">Basic</th>
                  {/* Earnings */}
                  <th className="px-2 py-1.5 text-right font-medium border-l border-slate-600">Earned</th>
                  <th className="px-2 py-1.5 text-right font-medium">OT</th>
                  <th className="px-2 py-1.5 text-right font-medium">ODW</th>
                  <th className="px-2 py-1.5 text-right font-medium">Incent.</th>
                  <th className="px-2 py-1.5 text-right font-medium">TA</th>
                  <th className="px-2 py-1.5 text-right font-medium">HRA</th>
                  <th className="px-2 py-1.5 text-right font-medium">DA</th>
                  <th className="px-2 py-1.5 text-right font-medium">Conv</th>
                  <th className="px-2 py-1.5 text-right font-medium">Shift</th>
                  <th className="px-2 py-1.5 text-right font-medium text-emerald-300">Gross</th>
                  {/* Deductions */}
                  <th className="px-2 py-1.5 text-right font-medium border-l border-slate-600">PF</th>
                  <th className="px-2 py-1.5 text-right font-medium">ESI</th>
                  <th className="px-2 py-1.5 text-right font-medium">PTAX</th>
                  <th className="px-2 py-1.5 text-right font-medium">MLWF</th>
                  <th className="px-2 py-1.5 text-right font-medium">TDS</th>
                  <th className="px-2 py-1.5 text-right font-medium">Adv.</th>
                  <th className="px-2 py-1.5 text-right font-medium">Other</th>
                  <th className="px-2 py-1.5 text-right font-medium text-rose-300">Total</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filteredSalaries.map((s, idx) => {
                  // Display paid days = D.O.P. = Work + W.Off + PL - LWP + ODW (from backend dop_days)
                  const displayPaidDays = s.dop_days || ((s.days_worked || 0) + (s.weekly_offs || 0) + (s.paid_leaves || 0) - (s.lwp || 0) + (s.odw_days || 0));
                  const earnedDays = s.earned_days || ((s.days_worked || 0) + (s.weekly_offs || 0) + (s.paid_leaves || 0) - (s.lwp || 0));
                  const earnedBasic = s.earned_basic || Math.round((s.basic_salary / (s.total_days || 1)) * earnedDays);
                  const isDraft = s.hr_status === 'Draft';
                  const isSelected = selectedIds.includes(s.id);
                  const isRejected = s.manager_status === 'Rejected' || s.ceo_status === 'Rejected';
                  const emp = employees.find(e => e.id === s.employee_id) || {};
                  const d = '-';
                  const c = s.comments || {};
                  const Cmt = ({text}) => text ? <span className="block text-[11px] text-indigo-500 italic truncate max-w-[80px]" title={text}>"{text}"</span> : null;
                  return (
                    <tr key={s.id} className={`hover:bg-slate-50 transition-colors ${isRejected ? 'bg-rose-50/60' : isSelected ? 'bg-indigo-50/60' : ''}`} data-testid={`v2-row-${s.id}`}>
                      <td className="px-2 py-2.5 text-center sticky left-0 bg-inherit z-10">
                        {user.role === 'HR' && isDraft && (
                          <Checkbox checked={isSelected} onCheckedChange={() => toggleSelect(s.id)} />
                        )}
                      </td>
                      <td className="px-2 py-2.5 text-center text-slate-500 font-mono">{idx + 1}</td>
                      <td className="px-3 py-2.5 text-left whitespace-nowrap sticky left-8 bg-inherit z-10">
                        <span className="font-semibold text-slate-900 block">{toTitleCase(s.employee_name)}</span>
                        {empTypes[emp.employee_type_id] && <span className="text-[11px] text-slate-400">{empTypes[emp.employee_type_id]}</span>}
                      </td>
                      {/* Attendance */}
                      <td className="px-2 py-2.5 text-right font-mono border-l border-slate-100">{s.days_worked || 0}</td>
                      <td className="px-2 py-2.5 text-right font-mono text-blue-600">{s.weekly_offs || 0}</td>
                      <td className="px-2 py-2.5 text-right font-mono text-emerald-600">{s.paid_leaves > 0 ? s.paid_leaves : <span className="text-slate-300">{d}</span>}<Cmt text={c.pl_comment} /></td>
                      <td className="px-2 py-2.5 text-right font-mono text-red-600">{s.lwp > 0 ? s.lwp : <span className="text-slate-300">{d}</span>}<Cmt text={c.lwp_comment} /></td>
                      <td className="px-2 py-2.5 text-right font-mono text-purple-600">{s.odw_days > 0 ? s.odw_days : <span className="text-slate-300">{d}</span>}<Cmt text={c.odw_comment} /></td>
                      <td className="px-2 py-2.5 text-right font-mono font-bold">{displayPaidDays}</td>
                      <td className="px-2 py-2.5 text-right font-mono">{fmt(s.basic_salary)}</td>
                      {/* Earnings */}
                      <td className="px-2 py-2.5 text-right font-mono border-l border-slate-100">{fmt(earnedBasic)}</td>
                      <td className="px-2 py-2.5 text-right font-mono">{s.ot_amount > 0 ? fmt(s.ot_amount) : <span className="text-slate-300">{d}</span>}<Cmt text={c.ot_comment} /></td>
                      <td className="px-2 py-2.5 text-right font-mono">{(s.off_day_amount || 0) > 0 ? fmt(s.off_day_amount) : <span className="text-slate-300">{d}</span>}</td>
                      <td className="px-2 py-2.5 text-right font-mono">{(s.allowances?.Incentive || 0) > 0 ? fmt(s.allowances.Incentive) : <span className="text-slate-300">{d}</span>}<Cmt text={c.incentive_comment} /></td>
                      <td className="px-2 py-2.5 text-right font-mono">{(s.allowances?.['Travel Allowance'] || 0) > 0 ? fmt(s.allowances['Travel Allowance']) : <span className="text-slate-300">{d}</span>}<Cmt text={c.ta_comment} /></td>
                      <td className="px-2 py-2.5 text-right font-mono">{(s.allowances?.HRA || 0) > 0 ? fmt(s.allowances.HRA) : <span className="text-slate-300">{d}</span>}</td>
                      <td className="px-2 py-2.5 text-right font-mono">{(s.allowances?.DA || s.earned_da || 0) > 0 ? fmt(s.allowances?.DA || s.earned_da) : <span className="text-slate-300">{d}</span>}</td>
                      <td className="px-2 py-2.5 text-right font-mono">{(s.allowances?.Conveyance || s.earned_conv || 0) > 0 ? fmt(s.allowances?.Conveyance || s.earned_conv) : <span className="text-slate-300">{d}</span>}</td>
                      <td className="px-2 py-2.5 text-center"><span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-semibold bg-violet-50 text-violet-700">{s.shift_hours || 8}h</span></td>
                      <td className="px-2 py-2.5 text-right font-mono font-semibold text-emerald-700">{fmt(s.gross_salary)}</td>
                      {/* Deductions */}
                      <td className="px-2 py-2.5 text-right font-mono border-l border-slate-100">{(s.deductions?.PF || 0) > 0 ? fmt(s.deductions.PF) : <span className="text-slate-300">{d}</span>}</td>
                      <td className="px-2 py-2.5 text-right font-mono">{(s.deductions?.ESI || 0) > 0 ? fmt(s.deductions.ESI) : <span className="text-slate-300">{d}</span>}</td>
                      <td className="px-2 py-2.5 text-right font-mono">{(s.deductions?.PTAX || 0) > 0 ? fmt(s.deductions.PTAX) : <span className="text-slate-300">{d}</span>}</td>
                      <td className="px-2 py-2.5 text-right font-mono">{(s.deductions?.MLWF || 0) > 0 ? fmt(s.deductions.MLWF) : <span className="text-slate-300">{d}</span>}</td>
                      <td className="px-2 py-2.5 text-right font-mono">{(s.deductions?.TDS || 0) > 0 ? fmt(s.deductions.TDS) : <span className="text-slate-300">{d}</span>}</td>
                      <td className="px-2 py-2.5 text-right font-mono">{(s.deductions?.Advance || 0) > 0 ? fmt(s.deductions.Advance) : <span className="text-slate-300">{d}</span>}<Cmt text={c.advance_comment} /></td>
                      <td className="px-2 py-2.5 text-right font-mono">{(s.deductions?.Other || 0) > 0 ? fmt(s.deductions.Other) : <span className="text-slate-300">{d}</span>}<Cmt text={c.deductions_comment} /></td>
                      <td className="px-2 py-2.5 text-right font-mono font-semibold text-rose-600">{fmt(s.total_deductions)}</td>
                      {/* Net + Status */}
                      <td className="px-2 py-2.5 text-right font-mono font-bold text-slate-900 border-l border-slate-100">&#8377;{fmt(s.net_salary)}</td>
                      <td className="px-2 py-2.5 text-center border-l border-slate-100">
                        <div className="flex flex-col gap-1 items-center">
                          <StatusBadge status={isDraft ? 'Draft' : s.hr_status === 'Submitted' ? 'Sent' : s.hr_status} />
                          <StatusBadge status={`M: ${s.manager_status}`} />
                          <StatusBadge status={`C: ${s.ceo_status}`} />
                          {isRejected && user.role === 'HR' && (
                            <button onClick={() => handleRevert(s.id)}
                              className="mt-1 flex items-center gap-1 px-2 py-1 rounded bg-amber-100 text-amber-700 hover:bg-amber-200 text-[11px] font-semibold">
                              <Pencil className="h-3 w-3" /> Edit
                            </button>
                          )}
                          {user.role === 'HR' && (
                            <button onClick={() => handleDelete(s.id, s.employee_name)}
                              className="mt-1 flex items-center gap-1 px-2 py-1 rounded bg-rose-50 text-rose-500 hover:bg-rose-100 text-[11px] font-semibold">
                              <Trash2 className="h-3 w-3" /> Delete
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
                {/* Missing employees - attendance not updated */}
                {missingEmployees.map((emp) => (
                  <tr key={`missing-${emp.id}`} className="bg-red-50/70" data-testid={`v2-missing-row-${emp.id}`}>
                    <td className="px-2 py-2.5 text-center sticky left-0 bg-red-50/70 z-10"></td>
                    <td className="px-2 py-2.5 text-center text-red-400 font-mono">{filteredSalaries.length + missingEmployees.indexOf(emp) + 1}</td>
                    <td className="px-3 py-2.5 text-left whitespace-nowrap sticky left-8 bg-red-50/70 z-10">
                      <span className="font-semibold text-red-700 block">{toTitleCase(emp.name)}</span>
                      {empTypes[emp.employee_type_id] && <span className="text-[11px] text-red-400">{empTypes[emp.employee_type_id]}</span>}
                    </td>
                    <td colSpan={23} className="px-4 py-2.5 text-left border-l border-red-100">
                      <div className="flex items-center gap-2">
                        <AlertTriangle className="h-3.5 w-3.5 text-red-500 flex-shrink-0" />
                        <span className="text-red-600 text-xs font-medium">Monthly Grid 2 not updated for this month</span>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="bg-slate-100 font-semibold text-xs border-t-2 border-slate-300">
                  <td className="px-2 py-3 sticky left-0 bg-slate-100 z-10"></td>
                  <td className="px-2 py-3"></td>
                  <td className="px-3 py-3 text-left text-slate-700 sticky left-8 bg-slate-100 z-10">TOTAL ({filteredSalaries.length} employees{missingEmployees.length > 0 ? `, ${missingEmployees.length} pending` : ''})</td>
                  {/* Attendance spacers */}
                  <td className="px-2 py-3 border-l border-slate-200"></td>
                  <td className="px-2 py-3"></td>
                  <td className="px-2 py-3"></td>
                  <td className="px-2 py-3"></td>
                  <td className="px-2 py-3"></td>
                  <td className="px-2 py-3"></td>
                  <td className="px-2 py-3 text-right font-mono">{fmt(totals.basic)}</td>
                  {/* Earnings */}
                  <td className="px-2 py-3 text-right font-mono border-l border-slate-200">{fmt(totals.earned)}</td>
                  <td className="px-2 py-3 text-right font-mono">{fmt(totals.ot_amount)}</td>
                  <td className="px-2 py-3 text-right font-mono">{fmt(totals.odw_amount)}</td>
                  <td className="px-2 py-3 text-right font-mono">{fmt(totals.incentive)}</td>
                  <td className="px-2 py-3 text-right font-mono">{fmt(totals.ta)}</td>
                  <td className="px-2 py-3 text-right font-mono">{fmt(totals.hra)}</td>
                  <td className="px-2 py-3 text-right font-mono">{fmt(totals.da)}</td>
                  <td className="px-2 py-3 text-right font-mono">{fmt(totals.conv)}</td>
                  <td className="px-2 py-3"></td>
                  <td className="px-2 py-3 text-right font-mono text-emerald-700">{fmt(totals.gross)}</td>
                  {/* Deductions */}
                  <td className="px-2 py-3 text-right font-mono border-l border-slate-200">{fmt(totals.pf)}</td>
                  <td className="px-2 py-3 text-right font-mono">{fmt(totals.esi)}</td>
                  <td className="px-2 py-3 text-right font-mono">{fmt(totals.ptax)}</td>
                  <td className="px-2 py-3 text-right font-mono">{fmt(totals.mlwf)}</td>
                  <td className="px-2 py-3 text-right font-mono">{fmt(totals.tds)}</td>
                  <td className="px-2 py-3 text-right font-mono">{fmt(totals.advance)}</td>
                  <td className="px-2 py-3 text-right font-mono">{fmt(totals.other)}</td>
                  <td className="px-2 py-3 text-right font-mono text-rose-600">{fmt(totals.totalDed)}</td>
                  <td className="px-2 py-3 text-right font-mono font-bold text-slate-900 border-l border-slate-200">&#8377;{fmt(totals.net)}</td>
                  <td className="px-2 py-3 border-l border-slate-200"></td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      )}

      {/* Bulk Edit Modal */}
      {bulkEditOpen && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center" onClick={() => setBulkEditOpen(false)}>
          <div className="bg-white rounded-2xl shadow-2xl w-[520px] max-h-[80vh] overflow-auto" onClick={e => e.stopPropagation()} data-testid="bulk-edit-modal">
            <div className="p-5 border-b border-slate-200">
              <h3 className="text-lg font-bold text-slate-900">Bulk Edit — {selectedIds.length} Employee(s)</h3>
              <p className="text-xs text-slate-500 mt-1">Only fill fields you want to change. Empty fields will be skipped.</p>
            </div>
            <div className="p-5 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs text-slate-600">Days Present</Label>
                  <Input type="number" step="0.5" placeholder="–" value={bulkEditFields.days_worked ?? ''} onChange={e => setBulkEditFields(p => ({...p, days_worked: e.target.value}))} data-testid="bulk-days-worked" />
                </div>
                <div>
                  <Label className="text-xs text-slate-600">Weekly Offs</Label>
                  <Input type="number" step="0.5" placeholder="–" value={bulkEditFields.weekly_offs ?? ''} onChange={e => setBulkEditFields(p => ({...p, weekly_offs: e.target.value}))} data-testid="bulk-weekly-offs" />
                </div>
                <div>
                  <Label className="text-xs text-slate-600">Paid Leaves (PL)</Label>
                  <Input type="number" step="0.5" placeholder="–" value={bulkEditFields.paid_leaves ?? ''} onChange={e => setBulkEditFields(p => ({...p, paid_leaves: e.target.value}))} data-testid="bulk-pl" />
                </div>
                <div>
                  <Label className="text-xs text-slate-600">LWP</Label>
                  <Input type="number" step="0.5" placeholder="–" value={bulkEditFields.lwp ?? ''} onChange={e => setBulkEditFields(p => ({...p, lwp: e.target.value}))} data-testid="bulk-lwp" />
                </div>
                <div>
                  <Label className="text-xs text-slate-600">OT Hours</Label>
                  <Input type="number" step="0.5" placeholder="–" value={bulkEditFields.ot_hours ?? ''} onChange={e => setBulkEditFields(p => ({...p, ot_hours: e.target.value}))} data-testid="bulk-ot" />
                </div>
                <div>
                  <Label className="text-xs text-slate-600">ODW Days</Label>
                  <Input type="number" step="0.5" placeholder="–" value={bulkEditFields.odw_days ?? ''} onChange={e => setBulkEditFields(p => ({...p, odw_days: e.target.value}))} data-testid="bulk-odw" />
                </div>
              </div>
              <div className="border-t border-slate-200 pt-4">
                <p className="text-xs font-semibold text-slate-600 mb-3">Financial Adjustments (₹)</p>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-xs text-slate-600">Incentive</Label>
                    <Input type="number" placeholder="–" value={bulkEditFields.incentive ?? ''} onChange={e => setBulkEditFields(p => ({...p, incentive: e.target.value}))} data-testid="bulk-incentive" />
                  </div>
                  <div>
                    <Label className="text-xs text-slate-600">T.A.</Label>
                    <Input type="number" placeholder="–" value={bulkEditFields.ta ?? ''} onChange={e => setBulkEditFields(p => ({...p, ta: e.target.value}))} data-testid="bulk-ta" />
                  </div>
                  <div>
                    <Label className="text-xs text-slate-600">Advance</Label>
                    <Input type="number" placeholder="–" value={bulkEditFields.advance ?? ''} onChange={e => setBulkEditFields(p => ({...p, advance: e.target.value}))} data-testid="bulk-advance" />
                  </div>
                  <div>
                    <Label className="text-xs text-slate-600">Other Deductions</Label>
                    <Input type="number" placeholder="–" value={bulkEditFields.deductions_other ?? ''} onChange={e => setBulkEditFields(p => ({...p, deductions_other: e.target.value}))} data-testid="bulk-deductions" />
                  </div>
                </div>
              </div>
            </div>
            <div className="p-5 border-t border-slate-200 flex justify-end gap-3">
              <Button variant="outline" onClick={() => setBulkEditOpen(false)}>Cancel</Button>
              <Button onClick={handleBulkEditSave} disabled={bulkSaving} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="bulk-edit-save">
                {bulkSaving ? 'Saving...' : `Save Changes for ${selectedIds.length} Employee(s)`}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SalaryProcess1;
